/**
 * Tela principal de reprodução.
 * Montada nos estados 'starting' e 'playing' — orquestra heartbeat,
 * schedule sync, play loop e renderização.
 *
 * Fluxo após pareamento:
 *   1. PlayerScreen monta em 'starting'
 *   2. useHeartbeat dispara imediatamente → popula workspaceId nas credenciais
 *   3. useScheduleSync dispara imediatamente → busca schedule
 *   4. Ao receber o schedule → setScreen('playing') → play loop inicia
 */
import { useEffect } from 'react'
import { playLogManager } from '@/engine/play-log-manager'
import { scheduleManager } from '@/engine/schedule-manager'
import { useHeartbeat } from '@/hooks/use-heartbeat'
import { usePlayLoop } from '@/hooks/use-play-loop'
import { useScheduleSync } from '@/hooks/use-schedule-sync'
import { ItemRenderer } from '@/renderers/item-renderer'
import { QrOverlay } from '@/renderers/qr-overlay'
import { usePlayerStore } from '@/store/player.store'
import { playerLogger } from '@/utils/player-logger'
import { FallbackScreen } from './fallback-screen'

export function PlayerScreen() {
  const screen = usePlayerStore((s) => s.screen)
  const { currentItem, playSeq, onItemEnded, triggerRecompile } = usePlayLoop()

  // Heartbeat e schedule sync iniciam imediatamente (inclusive em 'starting')
  useHeartbeat(currentItem ? { playlistId: currentItem.playlistId, assetId: currentItem.assetId } : null)
  useScheduleSync(triggerRecompile)

  useEffect(() => {
    playLogManager.start()
    const handleUnload = () => void playLogManager.flush()
    window.addEventListener('beforeunload', handleUnload)
    return () => {
      playLogManager.stop()
      window.removeEventListener('beforeunload', handleUnload)
    }
  }, [])

  // Restaura cache offline ao montar
  useEffect(() => {
    scheduleManager.loadFromCache()
  }, [])

  playerLogger.debug('[PlayerScreen] Render, screen:', screen, 'currentItem:', currentItem)

  // Em 'starting' ou sem item: tela de espera
  if (screen === 'starting' || !currentItem) {
    return <FallbackScreen />
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000',
        overflow: 'hidden'
      }}
    >
      <ItemRenderer
        // playSeq: itens seguidos com mesmo slot/asset precisam remontar (senão o player congela).
        key={`${currentItem.slotId}-${currentItem.assetId ?? currentItem.externalUrl}-${playSeq}`}
        item={currentItem}
        onEnd={onItemEnded}
      />
      {currentItem.qrCode && currentItem.eventAction !== 'screen_off' && <QrOverlay qr={currentItem.qrCode} />}
    </div>
  )
}
